import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Input,
  Chip,
  Pagination,
  Spinner,
  Button,
} from "@nextui-org/react";
import { FiSearch } from "react-icons/fi";
import { MdOutlineFileDownload } from "react-icons/md";
import { useNavigate, Link } from "react-router-dom";
import { displayDate, formatTime } from "../utils";
import { columns, statusOptions } from "../data/disputesData";
import { changeStatus } from "../store/slices/supplierSlice";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";import { STATUS } from "../store/constants";
import { refreshIfNeeded } from "../store/actions/refreshIfNeeded";
const INITIAL_VISIBLE_COLUMNS = ["status", "car", "part", "created"];
const BATCH_SIZE = 100;

const Disputes = () => {
  const accessToken = localStorage.getItem("accessToken");
  const role = localStorage.getItem("role");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { status } = useSelector((s) => s.supplier);

  // server-backed, batch-wise storage
  const [allDisputes, setAllDisputes] = useState([]); // concatenated backend batches
  const [batchPage, setBatchPage] = useState(0); // last backend batch loaded (1-based)
  const [allDataLoaded, setAllDataLoaded] = useState(false);
  const fetchingRef = useRef(false);

  // ui / controls
  const [filterValue, setFilterValue] = useState(""); // debounced value sent to server
  const [searchTerm, setSearchTerm] = useState(""); // immediate input
  const [visibleColumns] = useState(new Set(INITIAL_VISIBLE_COLUMNS));
  const [statusFilter] = useState("all");

  const [rowsPerPage, setRowsPerPage] = useState(6);
  const [page, setPage] = useState(() => {
    const stored = localStorage.getItem("currentPage");
    return stored ? Number(stored) : 1;
  });
  const [totalRecords, setTotalRecords] = useState(0);

  const [sortDescriptor, setSortDescriptor] = useState({
    column: null,
    direction: null,
  });

  // refs for stable callbacks
  const filterRef = useRef(filterValue);
  const sortRef = useRef(sortDescriptor);
  const allDataLoadedRef = useRef(allDataLoaded);
  const batchPageRef = useRef(batchPage);

  useEffect(() => {
    filterRef.current = filterValue;
  }, [filterValue]);
  useEffect(() => {
    sortRef.current = sortDescriptor;
  }, [sortDescriptor]);
  useEffect(() => {
    allDataLoadedRef.current = allDataLoaded;
  }, [allDataLoaded]);
  useEffect(() => {
    batchPageRef.current = batchPage;
  }, [batchPage]);

  // debounce timer ref
  const searchTimerRef = useRef(null);

  // stable fetchBatch: reads current filter/sort from refs if not provided
  const fetchBatch = useCallback(
    async (batchNum, { replace = false, filter, sort } = {}) => {
      const curFilter = typeof filter === "undefined" ? filterRef.current : filter;
      const curSort = typeof sort === "undefined" ? sortRef.current : sort;

      if (fetchingRef.current) return;
      if (allDataLoadedRef.current && !replace) return;

      try {
        fetchingRef.current = true;
      dispatch(changeStatus(STATUS.LOADING));
      await dispatch(refreshIfNeeded());
      const fresh = localStorage.getItem("accessToken");
      const auth = { headers: { Authorization: `Bearer ${fresh}` } };

      const qp = new URLSearchParams();
        qp.append("page", batchNum);
        qp.append("pageSize", BATCH_SIZE);

      if (curFilter) qp.append("search", curFilter);
        if (curSort?.column) {
          qp.append("sortBy", curSort.column);
          qp.append("sortOrder", curSort.direction === "ascending" ? "asc" : "desc");
      }

      const res = await axios.get(`${process.env.REACT_APP_SERVER_URL}/disputes/?${qp}`, auth);

        const fetchedRows = res.data?.details?.rows || [];
        const indexObj = res.data?.details?.index || {};
        const backendCurrentPage = indexObj.currentPage || batchNum;
        const backendTotalRecords = indexObj.totalRecords || 0;

        setTotalRecords(backendTotalRecords);
        // update total pages if you need to display elsewhere

        if (replace || batchNum === 1) {
          setAllDisputes(fetchedRows);
        } else {
          setAllDisputes((prev) => {
            const prevIds = new Set(prev.map((r) => r.id));
            const uniqueNew = fetchedRows.filter((r) => !prevIds.has(r.id));
            return [...prev, ...uniqueNew];
          });
        }

        setBatchPage(backendCurrentPage);
        batchPageRef.current = backendCurrentPage;

        if (fetchedRows.length < BATCH_SIZE) {
          setAllDataLoaded(true);
          allDataLoadedRef.current = true;
        } else {
          setAllDataLoaded(false);
          allDataLoadedRef.current = false;
        }

        dispatch(changeStatus(STATUS.IDLE));
      } catch (err) {
        dispatch(changeStatus(STATUS.ERROR));
        console.error("fetchBatch error:", err?.response?.data || err.message || err);
      } finally {
        fetchingRef.current = false;
      }
    },
    [dispatch]
  );

  // ensurePageData loads additional batches if user navigates past loaded data
  const ensurePageData = useCallback(
    async (uiPage) => {
      const requiredEndIndex = uiPage * rowsPerPage;
      if (requiredEndIndex <= allDisputes.length) return;
      if (allDataLoadedRef.current) return;

      const requiredBatch = Math.ceil(requiredEndIndex / BATCH_SIZE);
      let startBatch = batchPageRef.current || 0;
      for (let b = startBatch + 1; b <= requiredBatch; b++) {
        if (fetchingRef.current) {
          // wait until ongoing fetch finishes
          // small polling loop
          // eslint-disable-next-line no-await-in-loop
          await new Promise((res) => {
            const id = setInterval(() => {
              if (!fetchingRef.current) {
                clearInterval(id);
                res();
              }
            }, 50);
          });
        }
        // fetch batch
        // eslint-disable-next-line no-await-in-loop
        await fetchBatch(b, { replace: false });
        if (allDataLoadedRef.current) break;
      }
    },
    [allDisputes.length, rowsPerPage, fetchBatch]
  );

  // initial & when filter/sort/rowsPerPage change -> reset and fetch first batch
  useEffect(() => {
    setAllDisputes([]);
    setAllDataLoaded(false);
    setBatchPage(0);
    setPage(1);
    fetchBatch(1, { replace: true }).catch((e) => console.error(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue, sortDescriptor.column, sortDescriptor.direction, rowsPerPage]);

  // periodic refresh (optional)
  useEffect(() => {
    const id = setInterval(() => {
      if (!fetchingRef.current) fetchBatch(1, { replace: true }).catch(() => {});
    }, 120000);
    return () => clearInterval(id);
  }, [fetchBatch]);

  // debounce handlers for search input
  const onSearchChange = useCallback((val) => {
    setSearchTerm(val);
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    searchTimerRef.current = setTimeout(() => {
      setFilterValue(val.trim());
      setPage(1);
      localStorage.setItem("currentPage", 1);
      // reset batches will be handled by effect above
      searchTimerRef.current = null;
    }, 500);
  }, []);

  const onClear = useCallback(() => {
    setSearchTerm("");
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }
    setFilterValue("");
    setAllDisputes([]);
    setAllDataLoaded(false);
    setBatchPage(0);
    setPage(1);
    localStorage.setItem("currentPage", 1);
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = null;
      }
    };
  }, []);

  // client-side derived lists (we still respect server-side search; statusFilter is client)
  const filteredItems = useMemo(() => {
    let data = [...allDisputes];

    // keep client-side filter only for status (server search already applied)
    if (
      statusFilter !== "all" &&
      Array.from(statusFilter).length !== statusOptions.length
    ) {
      data = data.filter((d) =>
        statusFilter.includes(d?.QuoteStatus?.status?.toLowerCase())
      );
    }

    return data;
  }, [allDisputes, statusFilter]);

  // sorting (client-side sort on the currently loaded slice)
  const getSortValue = (item, column) => {
  switch (column) {
    case "status":
      return item.QuoteStatus?.status?.toLowerCase() || "";
    case "customer": {
      const firstName = item.Order?.Request?.User?.first_name || "";
      const lastName = item.Order?.Request?.User?.last_name || "";
      return `${firstName} ${lastName}`.toLowerCase();
      }
    case "car": {
      const make = item.Order?.Request?.VehicleMake?.name || item.Order?.Request?.custom_make || "";
      const model = item.Order?.Request?.VehicleModel?.name || item.Order?.Request?.custom_model || "";
      return `${make} ${model}`.toLowerCase().trim();
      }
    case "part":
      return item.Order?.Request?.Part?.name || item.Order?.Request?.custom_part_type || "";
    case "created":
      return new Date(item.createdAt || item.Order?.Request?.date_requested).getTime() || 0;
    default:
      return item[column] || "";
  }
};

const sortedItems = useMemo(() => {
  if (!sortDescriptor.column) return filteredItems;
  return [...filteredItems].sort((a, b) => {
    let v1 = getSortValue(a, sortDescriptor.column);
    let v2 = getSortValue(b, sortDescriptor.column);

    // date detection
    const isDate1 = !isNaN(Date.parse(v1));
    const isDate2 = !isNaN(Date.parse(v2));
    if (isDate1 && isDate2) {
      v1 = new Date(v1).getTime();
      v2 = new Date(v2).getTime();
    }

    const isNum1 = !isNaN(v1) && v1 !== "";
    const isNum2 = !isNaN(v2) && v2 !== "";
    if (isNum1 && isNum2) {
        return sortDescriptor.direction === "ascending" ? v1 - v2 : v2 - v1;
    }

    v1 = v1.toString().toLowerCase();
    v2 = v2.toString().toLowerCase();
    const cmp = v1.localeCompare(v2);
    return sortDescriptor.direction === "ascending" ? cmp : -cmp;
  });
}, [filteredItems, sortDescriptor]);

const pagedItems = useMemo(() => {
 const start = (page - 1) * rowsPerPage;
  return sortedItems.slice(start, start + rowsPerPage);
}, [sortedItems, page, rowsPerPage]);

  // pages: prefer server totalRecords if available
  const pages = Math.max(1, Math.ceil((totalRecords || filteredItems.length) / rowsPerPage));

  const headerColumns = useMemo(() => columns.filter((c) => visibleColumns.has(c.uid)), [visibleColumns]);

  const renderCell = useCallback((item, columnKey) => {
    switch (columnKey) {
      case "status":
        return (
          <Chip color="danger" variant="flat" className="capitalize">
            Disputed
          </Chip>
        );
      case "car":
        return (
          <p className="table_cell capitalize">
            {item.Order?.Request?.VehicleMake?.name?.toLowerCase() ||
              item.Order?.Request?.custom_make?.toLowerCase() ||
              "Not Provided"}{" "}
            {item.Order?.Request?.VehicleModel?.name?.toLowerCase() ||
              item.Order?.Request?.custom_model?.toLowerCase() ||
              "Not Provided"}
          </p>
        );
      case "part":
        return (
          <p className="table_cell capitalize">
            {item.Order?.Request?.Part?.name?.toLowerCase() ||
              item.Order?.Request?.custom_part_type?.toLowerCase() ||
              "Not Provided"}
          </p>
        );
      case "created":
        return (
          <div className="flex flex-col gap-1">
            <p className="table_cell capitalize">{formatTime(item.createdAt)}</p>
            <p className="table_cell_sm">{displayDate(item.createdAt)}</p>
          </div>
        );
      default:
        return item[columnKey];
    }
  }, []);

  // handlers
  const onRowsPerPageChange = useCallback((e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
    localStorage.setItem("currentPage", 1);
  }, []);

  const onSearchClear = useCallback(() => {
    onClear();
  }, [onClear]);

  const handleDownloadCSV = useCallback(() => {
    // construct CSV from current pagedItems
    const data = pagedItems;
    const csvRows = [];
    csvRows.push(["Status", "Car", "Part", "Created At"].join(","));
    data.forEach((d) => {
      csvRows.push(
        [
          "Disputed",
          `${d.Order?.Request?.VehicleMake?.name || d.Order?.Request?.custom_make || "N/A"} ${d.Order?.Request?.VehicleModel?.name || d.Order?.Request?.custom_model || "N/A"}`,
          d.Order?.Request?.Part?.name || d.Order?.Request?.custom_part_type || "N/A",
          new Date(d.createdAt).toLocaleString(),
        ]
          .map((v) => `"${v}"`)
          .join(",")
      );
    });
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "disputes_report.csv";
    a.click();
    URL.revokeObjectURL(url);
  }, [pagedItems]);

  // when page changes, ensure data present
  const handlePageChange = useCallback(
    (newPage) => {
      setPage(newPage);
      localStorage.setItem("currentPage", newPage);
      ensurePageData(newPage).catch((e) => console.error(e));
    },
    [ensurePageData]
  );

  return (
    <Table
      aria-label="Disputes Table"
      isHeaderSticky
      isStriped
      sortDescriptor={sortDescriptor}
      onSortChange={(newSort) => {
        setSortDescriptor(newSort);
        // update ref immediately via effect (sortRef will sync)
        setPage(1);
        localStorage.setItem("currentPage", 1);
      }}
      topContent={
        <div className="flex flex-col gap-4">
          {/* search + download */}
          <div className="w-full flex justify-between gap-3 items-end">
            <Input
              isClearable
              className="flex-1 sm:max-w-[44%]"
              placeholder="Search Disputes..."
              startContent={<FiSearch />}
              value={searchTerm}
              onClear={onSearchClear}
              onValueChange={onSearchChange}
            />

            {role === "SUPPLIER" && (
              <Button
                startContent={<MdOutlineFileDownload />}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm"
                onClick={handleDownloadCSV}
              >
                Download CSV
              </Button>
            )}
          </div>

          {/* rows selector */}
          <div className="flex justify-between items-center text-xs sm:text-sm">
            <span className="text-default-400">
              Total: {totalRecords} Disputes
            </span>
            <label className="flex items-center text-default-400">
              Rows per page:
              <select
                className="bg-transparent outline-none text-default-400"
                onChange={onRowsPerPageChange} value={rowsPerPage}>
                <option value="6">6</option>
                <option value="10">10</option>
                <option value="15">15</option>
              </select>
            </label>
          </div>
        </div>
      }
      topContentPlacement="outside"
      bottomContent={
        <div className="py-2 flex justify-center items-center">
          <Pagination
            isCompact
            showControls
            showShadow
            color="primary"
            page={page}
            total={pages}
            onChange={handlePageChange}
          />
        </div>
      }
      bottomContentPlacement="outside"
      classNames={{ td: "whitespace-nowrap" }}
    >  
      <TableHeader columns={headerColumns}>
        {(col) => <TableColumn key={col.uid}  allowsSorting={
        col.uid === "car" || 
        col.uid === "part" || 
        col.uid === "created"
      }>{col.name}</TableColumn>}
      </TableHeader>

      <TableBody
        items={pagedItems}
        emptyContent="No disputes found"
        isLoading={status === STATUS.LOADING && !allDisputes.length}
        loadingContent={<Spinner label="Loading..." />}>
        {(item) => (
          <TableRow
            key={item.id}
            onClick={() => navigate(`/support/${item.id}`)}
          >
            {(colKey) => (
              <TableCell>
                <Link to={`/disputes/${item.id}`}>
                  {renderCell(item, colKey)}
                </Link>
              </TableCell>
            )}
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

export default Disputes;
